import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useControlledState } from '@prophecy/utils/react/hooks';
import React from 'react';
import { CheckPenIcon, PlaceholderIcon } from '../../Icons';
import { Stack } from '../../Layout';
import { FeaturedIcon } from '../../Misc/FeaturedIcon';
import { TabVariant } from '../../Tabs';
import { theme } from '../../theme';
import { Tooltip } from '../../Tooltip';
import { Text } from '../../Typography';
import { StyledTabsRoot, StyledTabsList, StyledContentIcon } from '../sharedStyled';
import { tokens } from '../tokens';
import { StepSize, StepState, IconType } from '../types';
import { getStepState } from '../utils';
import { VerticalStepBase, VerticalConnectorWrapper, VerticalConnector, VerticalStepIconBase, VerticalNoWrapStackItem } from './styled';
export function VerticalStepper({ current, defaultCurrent, onChange, steps, filled, size = StepSize.sm, connector = true, iconType = IconType.default, ...rest }) {
    const [currentStep = 0, setCurrentStep] = useControlledState({
        value: current,
        defaultValue: defaultCurrent,
        onChange
    });
    const onTabChange = (value)=>{
        setCurrentStep(Number(value));
    };
    const renderStepIcon = (index, state, disabled)=>/*#__PURE__*/ _jsxs(VerticalConnectorWrapper, {
            $connector: connector,
            $iconType: iconType,
            $state: state,
            children: [
                iconType === IconType.default ? /*#__PURE__*/ _jsx(VerticalStepIconBase, {
                    $state: state,
                    children: state === StepState.completed ? /*#__PURE__*/ _jsx(CheckPenIcon, {
                        type: "extended",
                        size: "xs"
                    }) : /*#__PURE__*/ _jsx(StyledContentIcon, {
                        type: "default"
                    })
                }) : /*#__PURE__*/ _jsx(FeaturedIcon, {
                    variant: "modern",
                    size: "l",
                    icon: /*#__PURE__*/ _jsx(PlaceholderIcon, {
                        type: "default"
                    })
                }),
                index < steps.length - 1 && /*#__PURE__*/ _jsx(VerticalConnector, {
                    $state: state,
                    $connector: connector,
                    $iconType: iconType
                })
            ]
        });
    const renderStepContent = (title, label, details, state, disabled)=>/*#__PURE__*/ _jsxs(VerticalNoWrapStackItem, {
            $state: state,
            $orientation: "vertical",
            $iconType: iconType,
            children: [
                /*#__PURE__*/ _jsx(Text, {
                    level: size,
                    weight: theme.fontWeight.semiBold,
                    tone: disabled ? tokens.StepList.vertical.state.disabled.color : tokens.StepList.vertical.state[state].color,
                    children: title || label
                }),
                /*#__PURE__*/ _jsx(Text, {
                    level: size,
                    weight: theme.fontWeight.normal,
                    tone: disabled ? tokens.StepList.vertical.state.disabled.detailsColor : tokens.StepList.vertical.state[state].detailsColor,
                    children: details
                })
            ]
        });
    const renderStep = (step, index)=>{
        const { title, label, details, ...stepProps } = step;
        const state = getStepState(index, currentStep);
        const disabled = stepProps.disabled;
        return /*#__PURE__*/ _jsx(React.Fragment, {
            children: /*#__PURE__*/ _jsx(Tooltip, {
                title: title,
                children: /*#__PURE__*/ _jsx(VerticalStepBase, {
                    ...stepProps,
                    value: index.toString(),
                    $iconType: iconType,
                    $state: state,
                    children: /*#__PURE__*/ _jsxs(Stack, {
                        direction: "horizontal",
                        alignY: "center",
                        gap: tokens.StepList.vertical.gap,
                        children: [
                            renderStepIcon(index, state, !!disabled),
                            renderStepContent(title, label, details, state, !!disabled)
                        ]
                    })
                })
            })
        }, index);
    };
    return /*#__PURE__*/ _jsx(StyledTabsRoot, {
        value: currentStep.toString(),
        onValueChange: onTabChange,
        dir: "ltr",
        variant: TabVariant.underline,
        $orientation: "vertical",
        ...rest,
        children: /*#__PURE__*/ _jsx(StyledTabsList, {
            $orientation: "vertical",
            children: /*#__PURE__*/ _jsx(Stack, {
                direction: "vertical",
                alignY: "start",
                children: steps.map(renderStep)
            })
        })
    });
}
